/* =========================================================================
   Humble Housewives — Header chrome JS (classic script, no modules)
   Currently: the announcement-bar rotator.

   Reproduces the React AnnouncementBar loop (Components.jsx:446-471) in
   vanilla JS:
     - duplicate the first message row at the end → seamless upward loop
     - advance with setInterval (data-dwell seconds → ms)
     - translate the track by -step * rowHeight
     - on the duplicate, snap back to step 0 (transition off for the snap,
       re-enabled next frame)

   CLS-safe: never writes element height. Row height is MEASURED from the
   rendered row. No-op when reduced motion is requested OR there is <= 1
   message — the bar then renders static.
   ========================================================================= */

/* -------------------------------------------------------------------------
   Shared body scroll-lock — refcount manager (file scope, all IIFEs share it).
   The mobile drawer and the search sheet can BOTH be open at once on mobile
   (hamburger + search are sibling controls). Each used to add/remove the
   .overflow-hidden class directly, so whichever closed first released the lock
   while the other was still open. This refcount keeps the lock held until the
   LAST overlay closes. Replaces the raw .overflow-hidden class cart-drawer.js
   uses — lock()/unlock() must be called exactly once per open/close.
   ------------------------------------------------------------------------- */
window.HHScrollLock = window.HHScrollLock || (function () {
  var count = 0;
  return {
    lock:   function () { if (count++ === 0) document.body.classList.add('overflow-hidden'); },
    unlock: function () { if (count > 0 && --count === 0) document.body.classList.remove('overflow-hidden'); }
  };
})();

/* -------------------------------------------------------------------------
   Keyboard-modality flag — `html.hh-kbd` is present only while the user is
   navigating by keyboard (Tab). It's removed on any pointer (mouse/touch).
   The CSS scopes Dawn's focus-visible ring on HH chrome to `html.hh-kbd` so
   the ring shows for keyboard users but NOT on mouse/touch — and crucially NOT
   on the PROGRAMMATIC focus we move when opening the search sheet / drawer or
   restoring focus on close (which otherwise mis-fires :focus-visible and drew
   stray "highlight" boxes on touch). a11y preserved for real keyboard nav.
   ------------------------------------------------------------------------- */
(function () {
  'use strict';
  var root = document.documentElement;
  window.addEventListener('keydown', function (e) {
    if (e.key === 'Tab') root.classList.add('hh-kbd');
  }, true);
  function pointer() { root.classList.remove('hh-kbd'); }
  window.addEventListener('mousedown', pointer, true);
  window.addEventListener('touchstart', pointer, { capture: true, passive: true });
})();

(function () {
  'use strict';

  // Tear down a previously initialised bar: stop the interval and remove the
  // appended loop clone. Safe to call on an uninitialised bar (no-op).
  function teardownAnnounce(bar) {
    if (bar._hhInterval) {
      clearInterval(bar._hhInterval);
      bar._hhInterval = null;
    }
    var clone = bar.querySelector('[data-hh-announce-clone]');
    if (clone && clone.parentNode) {
      clone.parentNode.removeChild(clone);
    }
    bar._hhInit = false;
  }

  function initAnnounce(bar) {
    // Idempotent: fully tear down any prior init before rebuilding, so a
    // theme-editor re-render can't stack intervals or duplicate clones.
    teardownAnnounce(bar);

    var track = bar.querySelector('[data-hh-announce-track]');
    if (!track) return;

    var rows = track.children;
    var count = rows.length;

    // Guard: nothing to rotate with 0 or 1 message.
    if (count <= 1) return;

    // Guard: honour reduced-motion (no auto-rotation; stays on first message).
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    // Duplicate the first message row at the end for a seamless upward loop.
    // Mark it inert so screen readers don't announce message 1 twice and its
    // link isn't tabbable, and tag it so teardown can find and remove it.
    var clone = rows[0].cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-hh-announce-clone', '');
    var cloneLink = clone.querySelector('a');
    if (cloneLink) cloneLink.setAttribute('tabindex', '-1');
    track.appendChild(clone);

    var dwellSec = parseFloat(bar.getAttribute('data-dwell'));
    if (!dwellSec || dwellSec <= 0) dwellSec = 4;
    var dwellMs = dwellSec * 1000;

    var step = 0;

    function rowHeight() {
      // Measure from the rendered element — never write height.
      return rows[0].getBoundingClientRect().height || rows[0].offsetHeight;
    }

    bar._hhInterval = setInterval(function () {
      step += 1;
      track.style.transform = 'translateY(' + (-step * rowHeight()) + 'px)';

      if (step === count) {
        // Showing the duplicate first message — after the slide finishes,
        // snap back to step 0 with the transition disabled.
        window.setTimeout(function () {
          track.classList.add('is-instant');
          step = 0;
          track.style.transform = 'translateY(0px)';
          // Re-enable the transition on the next frame.
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              track.classList.remove('is-instant');
            });
          });
        }, 560);
      }
    }, dwellMs);

    bar._hhInit = true;
  }

  function initAll() {
    var bars = document.querySelectorAll('[data-hh-announce]');
    for (var i = 0; i < bars.length; i++) {
      initAnnounce(bars[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor re-renders sections without a full reload. Re-init on load
  // (idempotent — teardownAnnounce runs first) and tear down on unload.
  // Harmless on the live storefront where these events never fire.
  document.addEventListener('shopify:section:load', function (e) {
    var bar = e.target.querySelector('[data-hh-announce]');
    if (bar) initAnnounce(bar);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var bar = e.target.querySelector('[data-hh-announce]');
    if (bar) teardownAnnounce(bar);
  });
})();

/* =========================================================================
   Header bar — interaction hooks

   Remaining bindings wired in later tasks:
     - data-hh-search-open → search overlay (Task 6)
     - data-hh-menu-open  → mobile drawer (Task 5)
     - id="cart-icon-bubble" → Dawn <cart-drawer> auto-binds its open handler
       (Task 7); no JS needed here for that binding.
   ========================================================================= */

/* =========================================================================
   Mega-menu dropdown (Task 3) — hover AND keyboard, dark-ink scrim.

   Reproduces Components.jsx:775-823. Keyboard/mouse model:
     - Mouse: mouseenter on a trigger opens its panel; mouseleave on the
       header closes.
     - Keyboard: click/Enter/Space on a trigger TOGGLES its panel (button
       semantics fire click for Enter/Space). Mere focus does NOT open.
     - focusout on the header: close only when focus moved outside the header
       — so a user can Tab THROUGH the cards (focus still in header) without
       the panel collapsing.
     - Escape closes and returns focus to the active trigger.
   Maintains aria-expanded on each trigger and sets data-menu-open on the
   header (consumed by Task 4's over-hero behavior).

   Desktop-only: gated behind matchMedia('(min-width: 768px)'). On small
   screens the buttons still navigate / a later drawer takes over — this
   script no-ops there.

   CLS-safe: only toggles the [hidden] attribute + the data-menu-open
   attribute. Never writes element height.
   ========================================================================= */

(function () {
  'use strict';

  var DESKTOP = '(min-width: 768px)';

  function isDesktop() {
    return window.matchMedia && window.matchMedia(DESKTOP).matches;
  }

  function initMega(header) {
    if (!header || header._hhMegaInit) return;

    var triggers = header.querySelectorAll('[data-hh-nav][data-panel]');
    var panels = header.querySelectorAll('[data-hh-panel]');
    var scrim = header.querySelector('[data-hh-mega-scrim]');
    if (!triggers.length || !panels.length || !scrim) return;

    // Track the trigger of the currently open panel so Escape can restore focus.
    var activeTrigger = null;
    // Authoritative open state for THIS IIFE. Independent of data-menu-open
    // (which is a write-only CSS signal shared with the mobile-drawer IIFE) so
    // the two can't read each other's state on a shared attribute.
    var megaOpen = false;

    function triggerFor(key) {
      for (var i = 0; i < triggers.length; i++) {
        if (triggers[i].getAttribute('data-panel') === key) return triggers[i];
      }
      return null;
    }

    function openPanel(key) {
      var found = false;
      for (var i = 0; i < panels.length; i++) {
        var panel = panels[i];
        if (panel.getAttribute('data-hh-panel') === key) {
          panel.hidden = false;
          found = true;
        } else {
          panel.hidden = true;
        }
      }
      if (!found) return;

      scrim.hidden = false;

      for (var j = 0; j < triggers.length; j++) {
        var isOpen = triggers[j].getAttribute('data-panel') === key;
        triggers[j].setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }

      activeTrigger = triggerFor(key);
      megaOpen = true;
      // Shared header signal: also written by the mobile-drawer IIFE and read by
      // the over-hero scroll-flip CSS. Write-only here — never read back as state.
      header.setAttribute('data-menu-open', '');
    }

    function closeAll() {
      for (var i = 0; i < panels.length; i++) panels[i].hidden = true;
      scrim.hidden = true;
      for (var j = 0; j < triggers.length; j++) {
        triggers[j].setAttribute('aria-expanded', 'false');
      }
      megaOpen = false;
      // Shared header signal (see openPanel): also written by the mobile-drawer
      // IIFE and read by the over-hero scroll-flip CSS. Write-only signal.
      header.removeAttribute('data-menu-open');
      activeTrigger = null;
    }

    function isOpen() {
      return megaOpen;
    }

    // ── Per-trigger bindings: hover opens, click toggles. Focus does NOT
    //    open (keyboard users toggle with Enter/Space → click). ──
    for (var i = 0; i < triggers.length; i++) {
      (function (trigger) {
        var key = trigger.getAttribute('data-panel');

        trigger.addEventListener('mouseenter', function () {
          if (isDesktop()) openPanel(key);
        });

        // Click / Enter / Space toggle (Enter & Space fire click on a button).
        trigger.addEventListener('click', function (e) {
          if (!isDesktop()) return;
          e.preventDefault();
          if (trigger.getAttribute('aria-expanded') === 'true') {
            closeAll();
          } else {
            openPanel(key);
          }
        });
      })(triggers[i]);
    }

    // Hovering any NON-panel target in the bar (the Diagnostic link, the logo,
    // or the right-hand controls) closes an open panel — mirrors the mockup,
    // where onMouseEnter on those sets navOpen(null) (Components.jsx:753-758).
    // Without this, moving Shop/Learn → Diagnostic leaves the panel stuck open
    // (no mouseleave fires while the pointer stays within the header).
    var closers = header.querySelectorAll(
      '.hh-header__nav a:not([data-hh-nav]), .hh-header__logo-link, .hh-header__controls'
    );
    for (var ci = 0; ci < closers.length; ci++) {
      closers[ci].addEventListener('mouseenter', function () {
        if (isDesktop() && isOpen()) closeAll();
      });
    }

    // ── Header-level bindings ──

    // Close when the pointer leaves the whole header region.
    header.addEventListener('mouseleave', function () {
      if (isDesktop()) closeAll();
    });

    // Keep the panel open while focus stays inside the header (so the user can
    // Tab THROUGH the cards); close when focus leaves the header entirely
    // (relatedTarget null = focus left the document, e.g. Tab to URL bar).
    header.addEventListener('focusout', function (e) {
      if (!isDesktop() || !isOpen()) return;
      var next = e.relatedTarget;
      if (!next || !header.contains(next)) {
        closeAll();
      }
    });

    header._hhMegaInit = true;
    header._hhMegaClose = closeAll;
    // Expose state/actions so the document/window-level listeners (registered
    // once at IIFE scope below) can drive each header.
    header._hhMegaIsOpen = isOpen;
    header._hhMegaEscape = function () {
      if (!isOpen()) return;
      var toFocus = activeTrigger;
      closeAll();
      if (toFocus) toFocus.focus();
    };
  }

  function initAllMega() {
    var headers = document.querySelectorAll('[data-hh-header]');
    for (var i = 0; i < headers.length; i++) initMega(headers[i]);
  }

  // ── Document/window-level listeners — registered ONCE at IIFE scope ──
  // (NOT inside initMega). Otherwise shopify:section:load in the theme editor
  // would leak a duplicate document/window listener on every header re-render.
  // Each handler iterates the current headers and drives any that are open.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.keyCode !== 27) return;
    // Breakpoint-gate: the mega-menu only lives on desktop. Without this the
    // handler would cross-fire on mobile and close the drawer's shared signal.
    if (!isDesktop()) return;
    var headers = document.querySelectorAll('[data-hh-header]');
    for (var i = 0; i < headers.length; i++) {
      if (headers[i]._hhMegaEscape) headers[i]._hhMegaEscape();
    }
  });

  // Collapse on resize down to mobile so a panel can't be stranded open.
  window.addEventListener('resize', function () {
    if (isDesktop()) return;
    var headers = document.querySelectorAll('[data-hh-header]');
    for (var i = 0; i < headers.length; i++) {
      var h = headers[i];
      if (h._hhMegaIsOpen && h._hhMegaIsOpen() && h._hhMegaClose) h._hhMegaClose();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllMega);
  } else {
    initAllMega();
  }

  // Theme editor re-renders the header section without a full reload. Reset the
  // init flag and re-bind on load; close any open panel on unload.
  document.addEventListener('shopify:section:load', function (e) {
    var header = e.target.querySelector('[data-hh-header]');
    if (header) {
      header._hhMegaInit = false;
      initMega(header);
    }
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var header = e.target.querySelector('[data-hh-header]');
    if (header && header._hhMegaClose) header._hhMegaClose();
  });
})();

/* =========================================================================
   Scroll flip — REMOVED. The mockup header is `position: onHero ? "absolute"
   : "relative"` (Components.jsx) — over a hero it is transparent and simply
   scrolls away with the page; it never flips to a solid background on scroll.
   The old `.is-scrolled` toggle made the still-visible absolute header flash a
   light background as scrolling began (Mary flagged it). The solid look is kept
   only for the mockup's own triggers — :hover / :focus-within / [data-menu-open]
   (the `lightUI = onHero && !navHover && !navOpen && !menuOpen` negation), which
   are pure CSS and need no JS.
   ========================================================================= */

/* =========================================================================
   Mobile drawer (Task 5) — left-slide snow panel, light-linen scrim, accordion.

   Reproduces Components.jsx:673-737. Behavior:
     - The hamburger ([data-hh-menu-open]) toggles the drawer; its aria-expanded
       and aria-label flip. The icon swap (hamburger ↔ X) is pure CSS, driven by
       the header's data-menu-open attribute (no JS innerHTML writes).
     - Each accordion toggle expands/collapses its submenu (one open at a time,
       mirroring the mockup's openSection). Sets aria-expanded + is-open class.
     - Escape closes and returns focus to the hamburger; a scrim click closes.
     - Focus is trapped inside the open drawer; the rest of the header bars are
       aria-hidden while open and restored on close.
     - Mobile-only: gated behind matchMedia('(max-width: 767px)'); no-ops on
       desktop where the mega-menu takes over.

   CLS-safe: only toggles attributes/classes. The slide + accordion are CSS
   transitions; JS never writes element height.
   ========================================================================= */

(function () {
  'use strict';

  var MOBILE = '(max-width: 767px)';

  function isMobile() {
    return window.matchMedia && window.matchMedia(MOBILE).matches;
  }

  // Focusable elements within a container (for the focus trap).
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function focusable(container) {
    var nodes = container.querySelectorAll(FOCUSABLE);
    var out = [];
    for (var i = 0; i < nodes.length; i++) {
      // Skip elements hidden from layout.
      if (nodes[i].offsetParent !== null || nodes[i] === document.activeElement) {
        out.push(nodes[i]);
      }
    }
    return out;
  }

  function initDrawer(header) {
    if (!header || header._hhDrawerInit) return;

    var trigger = header.querySelector('[data-hh-menu-open]');
    var scrim = header.querySelector('[data-hh-mobile-scrim]');
    var panel = header.querySelector('[data-hh-mobile-panel]');
    if (!trigger || !scrim || !panel) return;

    // Children of the header (the bars + the desktop mega-menu nodes) that
    // should be hidden from AT + taken out of the tab order while the drawer is
    // open. We toggle aria-hidden on those, never on the panel or scrim.
    function backgroundNodes() {
      var out = [];
      var kids = header.children;
      for (var i = 0; i < kids.length; i++) {
        if (kids[i] !== scrim && kids[i] !== panel) out.push(kids[i]);
      }
      return out;
    }

    function setBackgroundHidden(hidden) {
      var nodes = backgroundNodes();
      for (var i = 0; i < nodes.length; i++) {
        if (hidden) {
          nodes[i].setAttribute('aria-hidden', 'true');
        } else {
          nodes[i].removeAttribute('aria-hidden');
        }
      }
    }

    // Authoritative drawer state is the panel's own visibility — NOT the shared
    // data-menu-open attribute (which the mega-menu IIFE also writes; reading it
    // here let the mega Escape handler's removeAttribute trick this into a
    // false-negative on mobile). data-menu-open stays a write-only CSS signal.
    function isOpen() {
      return !panel.hidden;
    }

    function openDrawer() {
      if (isOpen()) return;
      panel.hidden = false;
      scrim.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      trigger.setAttribute('aria-label', 'Close menu');
      // Shared header signal: also written by the desktop mega-menu IIFE and
      // read by the over-hero scroll-flip CSS. Breakpoint-exclusive with the
      // mega-menu, so no live collision — keep them in sync if either changes.
      header.setAttribute('data-menu-open', '');
      // Lock page scroll so the background can't scroll under the open drawer.
      // Routed through the shared HHScrollLock refcount manager so the drawer
      // and the search sheet (both openable at once on mobile) don't release
      // each other's lock. lock() called exactly once per open.
      // Mobile-only breakpoint where scrollbars are overlay, so no width compensation.
      window.HHScrollLock.lock();
      setBackgroundHidden(true);
      // Move focus into the drawer (first link) for keyboard users.
      var f = focusable(panel);
      if (f.length) f[0].focus();
    }

    function closeDrawer(restoreFocus) {
      if (!isOpen()) return;
      panel.hidden = true;
      scrim.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-label', 'Open menu');
      // Shared header signal (see openDrawer): also written by the desktop
      // mega-menu IIFE and read by the over-hero scroll-flip CSS.
      header.removeAttribute('data-menu-open');
      // Release the page scroll lock added in openDrawer via the shared
      // HHScrollLock refcount manager. Every close path runs through here (scrim
      // click, Escape, resize-to-desktop, section unload), so unlock() is called
      // exactly once per open — and the body class only clears once the search
      // sheet (if also open) has released its own lock too.
      window.HHScrollLock.unlock();
      setBackgroundHidden(false);
      // Collapse any open accordion section so it reopens fresh next time.
      var subs = panel.querySelectorAll('[data-hh-mobile-sub]');
      for (var i = 0; i < subs.length; i++) subs[i].classList.remove('is-open');
      var allToggles = panel.querySelectorAll('[data-hh-mobile-toggle]');
      for (var j = 0; j < allToggles.length; j++) {
        allToggles[j].setAttribute('aria-expanded', 'false');
      }
      if (restoreFocus !== false) trigger.focus();
    }

    // ── Hamburger toggles the drawer ──
    trigger.addEventListener('click', function (e) {
      if (!isMobile()) return;
      e.preventDefault();
      if (isOpen()) {
        closeDrawer(true);
      } else {
        openDrawer();
      }
    });

    // ── Scrim click closes ──
    scrim.addEventListener('click', function () {
      closeDrawer(true);
    });

    // ── Accordion toggles (one open at a time) ──
    var toggles = panel.querySelectorAll('[data-hh-mobile-toggle]');
    for (var t = 0; t < toggles.length; t++) {
      (function (toggle) {
        var subId = toggle.getAttribute('aria-controls');
        var sub = subId ? panel.querySelector('#' + subId) : null;
        if (!sub) return;
        toggle.addEventListener('click', function () {
          var willOpen = toggle.getAttribute('aria-expanded') !== 'true';
          // Close all others (single-open accordion, mirrors openSection).
          for (var k = 0; k < toggles.length; k++) {
            if (toggles[k] === toggle) continue;
            toggles[k].setAttribute('aria-expanded', 'false');
            var oid = toggles[k].getAttribute('aria-controls');
            var os = oid ? panel.querySelector('#' + oid) : null;
            if (os) os.classList.remove('is-open');
          }
          toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
          sub.classList.toggle('is-open', willOpen);
        });
      })(toggles[t]);
    }

    // ── Focus trap — keep Tab cycling inside the open panel ──
    panel.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' && e.keyCode !== 9) return;
      if (!isOpen()) return;
      var f = focusable(panel);
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first || !panel.contains(document.activeElement)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    header._hhDrawerInit = true;
    header._hhDrawerClose = function () { closeDrawer(false); };
    header._hhDrawerEscape = function () {
      if (isOpen()) closeDrawer(true);
    };
    header._hhDrawerIsOpen = isOpen;
  }

  function initAllDrawers() {
    var headers = document.querySelectorAll('[data-hh-header]');
    for (var i = 0; i < headers.length; i++) initDrawer(headers[i]);
  }

  // ── Document/window-level listeners — registered ONCE at IIFE scope ──
  // (not inside initDrawer, so a theme-editor re-render can't leak duplicates).
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.keyCode !== 27) return;
    // Breakpoint-gate: the drawer only lives on mobile. Without this the handler
    // would cross-fire on desktop alongside the mega-menu's Escape handler.
    if (!isMobile()) return;
    var headers = document.querySelectorAll('[data-hh-header]');
    for (var i = 0; i < headers.length; i++) {
      if (headers[i]._hhDrawerEscape) headers[i]._hhDrawerEscape();
    }
  });

  // Collapse on resize up to desktop so the drawer can't be stranded open.
  window.addEventListener('resize', function () {
    if (isMobile()) return;
    var headers = document.querySelectorAll('[data-hh-header]');
    for (var i = 0; i < headers.length; i++) {
      var h = headers[i];
      if (h._hhDrawerIsOpen && h._hhDrawerIsOpen() && h._hhDrawerClose) h._hhDrawerClose();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllDrawers);
  } else {
    initAllDrawers();
  }

  // Theme editor re-renders the header section without a full reload. Reset the
  // init flag and re-bind on load; close any open drawer on unload.
  document.addEventListener('shopify:section:load', function (e) {
    var header = e.target.querySelector('[data-hh-header]');
    if (header) {
      header._hhDrawerInit = false;
      initDrawer(header);
    }
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var header = e.target.querySelector('[data-hh-header]');
    if (header && header._hhDrawerClose) header._hhDrawerClose();
  });
})();

/* =========================================================================
   Predictive search top-sheet (Task 6) — Components.jsx:827-861.

   A full-width sheet descends from the top with a light-linen scrim over the
   page; inside it lives Dawn's <predictive-search> (real Shopify results),
   styled to HH, with a "Trending now" default before the user types.

   Behavior:
     - Either [data-hh-search-open] trigger (desktop controls + mobile bar)
       opens the sheet: show panel + scrim, focus the input, lock body scroll,
       hide the background from AT, set the trigger aria-expanded.
     - Escape / scrim click / × button ([data-hh-search-close]) close: restore
       focus to the trigger that opened it, release the scroll lock, un-hide the
       background.
     - Focus is trapped inside the open sheet.
     - Breakpoint-agnostic — search works at all widths. State is the panel's
       OWN visibility (!panel.hidden), independent of the shared data-menu-open
       attribute (that belongs to the menus).
     - Dawn's <predictive-search> is reused verbatim; we never touch its
       element/input/results bindings — only open/close the wrapper.

   CLS-safe: only toggles attributes/classes. The descend + fade are CSS
   transitions; JS never writes element height.
   ========================================================================= */

(function () {
  'use strict';

  // Focusable elements within a container (for the focus trap).
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function focusable(container) {
    var nodes = container.querySelectorAll(FOCUSABLE);
    var out = [];
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].offsetParent !== null || nodes[i] === document.activeElement) {
        out.push(nodes[i]);
      }
    }
    return out;
  }

  function initSearch(header) {
    if (!header || header._hhSearchInit) return;

    var triggers = header.querySelectorAll('[data-hh-search-open]');
    var scrim = header.querySelector('[data-hh-search-scrim]');
    var panel = header.querySelector('[data-hh-search-panel]');
    if (!triggers.length || !scrim || !panel) return;

    var input = panel.querySelector('input[type="search"]');
    var closeBtn = panel.querySelector('[data-hh-search-close]');

    // The trigger that opened the sheet — so close can restore focus to it.
    var lastTrigger = null;

    // Header children to hide from AT + take out of the tab order while open
    // (everything except the search scrim + panel). We toggle aria-hidden on
    // those, never on the panel or scrim.
    function backgroundNodes() {
      var out = [];
      var kids = header.children;
      for (var i = 0; i < kids.length; i++) {
        if (kids[i] !== scrim && kids[i] !== panel) out.push(kids[i]);
      }
      return out;
    }

    function setBackgroundHidden(hidden) {
      var nodes = backgroundNodes();
      for (var i = 0; i < nodes.length; i++) {
        if (hidden) {
          nodes[i].setAttribute('aria-hidden', 'true');
        } else {
          nodes[i].removeAttribute('aria-hidden');
        }
      }
    }

    // Authoritative open state is the panel's OWN visibility — NOT the shared
    // data-menu-open attribute (which the menu IIFEs write). Search never reads
    // or writes data-menu-open.
    function isOpen() {
      return !panel.hidden;
    }

    function openSearch(trigger) {
      if (isOpen()) return;
      lastTrigger = trigger || null;
      panel.hidden = false;
      scrim.hidden = false;
      for (var i = 0; i < triggers.length; i++) {
        triggers[i].setAttribute('aria-expanded', 'true');
      }
      // Lock page scroll via the shared HHScrollLock refcount manager (the
      // search sheet and the mobile drawer can both be open at once, so they
      // share one refcount and don't release each other's lock). lock() called
      // exactly once per open.
      window.HHScrollLock.lock();
      setBackgroundHidden(true);
      // Focus the input for keyboard users (mockup autoFocus).
      if (input) {
        input.focus();
      } else {
        var f = focusable(panel);
        if (f.length) f[0].focus();
      }
    }

    function closeSearch(restoreFocus) {
      if (!isOpen()) return;
      panel.hidden = true;
      scrim.hidden = true;
      for (var i = 0; i < triggers.length; i++) {
        triggers[i].setAttribute('aria-expanded', 'false');
      }
      // Release the scroll lock via the shared HHScrollLock refcount manager.
      // unlock() called exactly once per open; the body class only clears once
      // the mobile drawer (if also open) has released its lock too.
      window.HHScrollLock.unlock();
      setBackgroundHidden(false);
      if (restoreFocus !== false && lastTrigger) lastTrigger.focus();
      lastTrigger = null;
    }

    // ── Triggers open the sheet ──
    for (var t = 0; t < triggers.length; t++) {
      (function (trigger) {
        trigger.addEventListener('click', function (e) {
          e.preventDefault();
          if (isOpen()) {
            closeSearch(true);
          } else {
            openSearch(trigger);
          }
        });
      })(triggers[t]);
    }

    // ── Scrim click closes ──
    scrim.addEventListener('click', function () {
      closeSearch(true);
    });

    // ── × button closes ──
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        closeSearch(true);
      });
    }

    // ── Focus trap — keep Tab cycling inside the open sheet ──
    panel.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' && e.keyCode !== 9) return;
      if (!isOpen()) return;
      var f = focusable(panel);
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first || !panel.contains(document.activeElement)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    header._hhSearchInit = true;
    header._hhSearchClose = function () { closeSearch(false); };
    header._hhSearchEscape = function () {
      if (isOpen()) closeSearch(true);
    };
    header._hhSearchIsOpen = isOpen;
  }

  function initAllSearch() {
    var headers = document.querySelectorAll('[data-hh-header]');
    for (var i = 0; i < headers.length; i++) initSearch(headers[i]);
  }

  // ── Document-level Escape — registered ONCE at IIFE scope (not inside
  //    initSearch, so a theme-editor re-render can't leak duplicates).
  //    Breakpoint-agnostic; only acts when THIS sheet is open (guarded on the
  //    panel's own state, never on the shared data-menu-open attribute). ──
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.keyCode !== 27) return;
    var headers = document.querySelectorAll('[data-hh-header]');
    for (var i = 0; i < headers.length; i++) {
      if (headers[i]._hhSearchEscape) headers[i]._hhSearchEscape();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllSearch);
  } else {
    initAllSearch();
  }

  // Theme editor re-renders the header section without a full reload. Reset the
  // init flag and re-bind on load; close any open sheet on unload.
  document.addEventListener('shopify:section:load', function (e) {
    var header = e.target.querySelector('[data-hh-header]');
    if (header) {
      header._hhSearchInit = false;
      initSearch(header);
    }
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var header = e.target.querySelector('[data-hh-header]');
    if (header && header._hhSearchClose) header._hhSearchClose();
  });
})();

/* =========================================================================
   Cart integration (Task 7) — open the Dawn <cart-drawer> from the MOBILE
   cart control + keep the mobile cart bubble count fresh.

   Why this exists:
     - Dawn's <cart-drawer> auto-binds its open handler to id="cart-icon-bubble"
       (the DESKTOP cart link) and re-renders only that one bubble on cart
       change. The MOBILE cart link can't reuse that id (ids must be unique), so
       it's a second bubble that Dawn never wires or refreshes.

   What we do (small, additive, classic JS — we do NOT fork Dawn's cart JS):
     1. Mobile cart click → close any open HH overlay (drawer/search), then call
        cart-drawer.open(trigger). Falls back to the href if no <cart-drawer>.
     2. On every `cart-update` pubsub event, fetch /cart.js and sync the mobile
        bubble's count from item_count (creating/removing the count span).

   Scroll-lock coexistence (documented choice):
     Dawn's cart-drawer.js toggles `document.body.classList` 'overflow-hidden'
     DIRECTLY, while the HH mobile drawer + search refcount that SAME class via
     window.HHScrollLock. Mixing a direct toggle with a refcount can leave the
     lock stuck. Rather than fork Dawn's JS to intercept its lock, we enforce
     ONE overlay at a time: opening the cart first closes the mobile drawer and
     search (each runs its own HHScrollLock.unlock(), draining the refcount to 0
     and clearing the class). Dawn then owns the class alone for the cart's
     lifetime and clears it cleanly on close — no refcount collision.

   `subscribe` / `PUB_SUB_EVENTS` are BARE-SCOPE globals (constants.js +
   pubsub.js) — referenced bare, never window.*.
   ========================================================================= */
(function () {
  'use strict';

  // Close any open HH overlay so only one overlay is ever open at a time. Each
  // close path drains its own HHScrollLock refcount, so the body lock is clean
  // before Dawn's cart drawer takes it over.
  function closeHHOverlays() {
    var headers = document.querySelectorAll('[data-hh-header]');
    for (var i = 0; i < headers.length; i++) {
      if (headers[i]._hhDrawerClose) headers[i]._hhDrawerClose();
      if (headers[i]._hhSearchClose) headers[i]._hhSearchClose();
    }
  }

  // Wire every mobile cart control to open the Dawn <cart-drawer>.
  function initMobileCartOpen() {
    var controls = document.querySelectorAll('[data-hh-cart-mobile]');
    for (var i = 0; i < controls.length; i++) {
      (function (ctl) {
        if (ctl._hhCartBound) return;
        ctl._hhCartBound = true;
        ctl.addEventListener('click', function (e) {
          var drawer = document.querySelector('cart-drawer');
          // No drawer (cart_type != drawer) → let the link navigate to /cart.
          if (!drawer || typeof drawer.open !== 'function') return;
          e.preventDefault();
          closeHHOverlays();
          drawer.open(ctl);
        });
      })(controls[i]);
    }
  }

  // Localized, pluralized sr-only cart text matching hh-cart-bubble.liquid.
  // The Liquid exposes the cart_count locale string (singular + plural forms,
  // with the number replaced by a [count] placeholder) on the control via
  // data-cart-count-one / -other. We pick the form by count and substitute the
  // live number — so JS output reads identically to first paint in any locale.
  // Falls back to correct English pluralization if the attributes are absent.
  function cartCountText(ctl, itemCount) {
    var tpl = itemCount === 1
      ? ctl.getAttribute('data-cart-count-one')
      : ctl.getAttribute('data-cart-count-other');
    if (tpl) return tpl.replace('[count]', itemCount);
    return itemCount + (itemCount === 1 ? ' item' : ' items');
  }

  // Sync the mobile bubble count from a cart object (item_count). Mirrors the
  // hh-cart-bubble snippet markup (visible aria-hidden count + localized sr-only
  // text) so the JS-updated bubble is structurally identical to first paint.
  function syncMobileCount(itemCount) {
    var controls = document.querySelectorAll('[data-hh-cart-mobile]');
    for (var i = 0; i < controls.length; i++) {
      var ctl = controls[i];
      ctl.setAttribute('aria-label', 'Cart (' + itemCount + ')');
      var badge = ctl.querySelector('.hh-header__cart-count');
      if (itemCount > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'hh-header__cart-count';
          ctl.appendChild(badge);
        }
        badge.innerHTML =
          '<span aria-hidden="true">' + itemCount + '</span>' +
          '<span class="hh-sr-only">' + cartCountText(ctl, itemCount) + '</span>';
      } else if (badge) {
        badge.parentNode.removeChild(badge);
      }
    }
  }

  function init() {
    initMobileCartOpen();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (c) { syncMobileCount(c.item_count); })
          .catch(function () { /* leave last-known count */ });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders the header without a full reload — re-bind controls.
  document.addEventListener('shopify:section:load', function () {
    initMobileCartOpen();
  });
})();


/* =========================================================================
   Diagnostic floating bar — scroll reveal + dismiss (session).

   Reproduces Components.jsx DiagnosticBar (867–918):
   • Reveal the bar (.is-visible) once scroll progress passes reveal_pct;
     hide again near the top. Passive listener, rAF-throttled.
   • Dismiss (data-hh-diag-dismiss) eases it down (.is-dismissed) and sets a
     sessionStorage flag so it stays dismissed for the session; on load, a set
     flag means the bar never reveals.
   • Reduced motion: appear without slide (CSS drops the transition; JS is the
     same — just class toggles).

   CLS-safe: position:fixed (CSS) + class toggles only. No height/inline writes.
   ========================================================================= */
(function () {
  'use strict';

  var DISMISS_KEY = 'hh_diag_dismissed';
  var ticking = false;

  function isDismissed() {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function setDismissed() {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch (e) {
      /* sessionStorage unavailable (private mode) — dismiss for this view only */
    }
  }

  function applyState() {
    ticking = false;
    var bar = document.querySelector('[data-hh-diag]');
    if (!bar) return;
    if (isDismissed()) {
      bar.classList.remove('is-visible');
      return;
    }
    var pct = parseFloat(bar.getAttribute('data-reveal-pct'));
    if (isNaN(pct)) pct = 25;
    var threshold = pct / 100;

    var sc = window.scrollY || document.documentElement.scrollTop || 0;
    var docH = document.documentElement.scrollHeight - window.innerHeight;
    var show = docH > 0 && sc / docH > threshold;
    bar.classList.toggle('is-visible', show);
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(applyState);
  }

  function onClick(e) {
    var trigger = e.target.closest && e.target.closest('[data-hh-diag-dismiss]');
    if (!trigger) return;
    var bar = trigger.closest('[data-hh-diag]');
    if (!bar) return;
    setDismissed();
    bar.classList.remove('is-visible');
    bar.classList.add('is-dismissed');
  }

  function init() {
    // Delegate the dismiss click (bind once; survives section re-render).
    document.removeEventListener('click', onClick);
    document.addEventListener('click', onClick);
    applyState();
  }

  window.addEventListener('scroll', onScroll, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders the section without a full reload — re-evaluate.
  document.addEventListener('shopify:section:load', function () {
    init();
  });
})();
